import type { FinesSymbol, SoilFractionsContract } from "@/contracts";
import { roundToPrecision } from "@/utils";
import { roundToDisplay } from "@/utils/formatNumber";
import { subfractionWord } from "./subfraction";
import type { SoilDescriptionResult } from "./types";

const FINE_GROUP_NAME: Record<FinesSymbol, string> = {
  CL: "lean CLAY",
  ML: "SILT",
  CH: "fat CLAY",
  MH: "elastic SILT",
  "CL-ML": "silty CLAY",
};

function result(name: string, symbol: string): SoilDescriptionResult {
  return { description: `${name.charAt(0).toUpperCase()}${name.slice(1)} (${symbol})`, classification: symbol };
}

function isWellGraded(primaryIsSand: boolean, cu: number, cc: number): boolean {
  return cu >= (primaryIsSand ? 6 : 4) && cc >= 1 && cc <= 3;
}

function withParts(parts: string[]): string | null {
  if (parts.length === 0) return null;
  if (parts.length === 1) return `with ${parts[0]}`;
  return `with ${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

function named(head: string, parts: string[]): string {
  const tail = withParts(parts);
  return tail ? `${head} ${tail}` : head;
}

function gravelRangeWord(fine: number, coarse: number): string | null {
  const hasFine = fine >= 1;
  const hasCoarse = coarse >= 1;
  if (hasFine && hasCoarse) return "fine to coarse";
  if (hasFine) return "fine";
  if (hasCoarse) return "coarse";
  return null;
}

function describeFineGrained(
  symbol: FinesSymbol,
  sand: number,
  gravel: number,
  cobbles: string[],
): SoilDescriptionResult {
  const plusNo200 = sand + gravel;
  const predominantlySand = sand >= gravel;
  const predominant = predominantlySand ? "sand" : "gravel";
  const other = predominantlySand ? "gravel" : "sand";
  const name = FINE_GROUP_NAME[symbol];
  if (plusNo200 >= 30) {
    const withOther = (predominantlySand ? gravel : sand) >= 15 ? [other] : [];
    return result(named(`${predominantlySand ? "sandy" : "gravelly"} ${name}`, [...withOther, ...cobbles]), symbol);
  }
  const withPredominant = plusNo200 >= 15 ? [predominant] : [];
  return result(named(name, [...withPredominant, ...cobbles]), symbol);
}

function passingCobbleSieve(f: SoilFractionsContract): SoilFractionsContract | null {
  const gravel = Math.min(f.gravel, f.fineGravel + f.mediumGravel + f.coarseGravel);
  const total = f.clay + f.silt + f.sand + gravel;
  if (total <= 0) return null;
  const share = (value: number) => roundToPrecision((value * 100) / total);
  return { ...f, clay: share(f.clay), silt: share(f.silt), sand: share(f.sand), gravel: share(gravel), cobbles: 0 };
}

function hasCobbles(f: SoilFractionsContract): boolean {
  const aboveSieve = f.gravel - Math.min(f.gravel, f.fineGravel + f.mediumGravel + f.coarseGravel);
  return roundToPrecision(aboveSieve + f.cobbles) >= 1;
}

function gravelBreakdown(f: SoilFractionsContract): string | null {
  const gravel = roundToPrecision(f.fineGravel + f.mediumGravel + f.coarseGravel);
  if (gravel < 1) return null;
  const range = gravelRangeWord(f.fineGravel, f.coarseGravel);
  const shown = roundToDisplay(gravel, 0);
  return range ? `${shown} % ${range} gravel` : `${shown} % gravel`;
}

export function describeSoilAstm(
  measured: SoilFractionsContract,
  symbol: FinesSymbol | null,
): SoilDescriptionResult | null {
  const named = describeGroupName(measured, symbol);
  const breakdown = gravelBreakdown(measured);
  if (named == null || breakdown == null) return named;
  return { ...named, description: `${named.description} — ${breakdown}` };
}

function describeGroupName(measured: SoilFractionsContract, symbol: FinesSymbol | null): SoilDescriptionResult | null {
  const f = passingCobbleSieve(measured);
  if (f == null) return null;
  const cobbles = hasCobbles(measured) ? ["cobbles"] : [];
  const fines = f.clay + f.silt;

  if (fines >= 50) {
    return symbol == null ? null : describeFineGrained(symbol, f.sand, f.gravel, cobbles);
  }

  const primaryIsSand = f.sand >= f.gravel;
  const letter = primaryIsSand ? "S" : "G";
  const noun = primaryIsSand ? "SAND" : "GRAVEL";
  const subfraction = primaryIsSand
    ? subfractionWord({ fine: f.fineSand, medium: f.mediumSand, coarse: f.coarseSand })
    : null;
  const primaryNoun = subfraction ? `${subfraction} ${noun}` : noun;
  const otherCoarse = (primaryIsSand ? f.gravel : f.sand) >= 15 ? [primaryIsSand ? "gravel" : "sand"] : [];
  const finesLetter = symbol == null ? null : symbol === "ML" || symbol === "MH" ? "M" : "C";

  if (fines > 12) {
    if (finesLetter == null) return null;
    const groupSymbol = symbol === "CL-ML" ? `${letter}C-${letter}M` : `${letter}${finesLetter}`;
    const adjective = symbol === "CL-ML" ? "silty, clayey" : finesLetter === "M" ? "silty" : "clayey";
    return result(named(`${adjective} ${primaryNoun}`, [...otherCoarse, ...cobbles]), groupSymbol);
  }

  if (f.cu == null || f.cc == null || !Number.isFinite(f.cu) || !Number.isFinite(f.cc)) return null;
  const well = isWellGraded(primaryIsSand, f.cu, f.cc);
  const gradingSymbol = `${letter}${well ? "W" : "P"}`;
  const gradingName = `${well ? "well" : "poorly"}-graded ${primaryNoun}`;

  if (fines < 5) {
    return result(named(gradingName, [...otherCoarse, ...cobbles]), gradingSymbol);
  }

  if (finesLetter == null) return null;
  const finesNoun = symbol === "CL-ML" ? "silty clay" : finesLetter === "M" ? "silt" : "clay";
  return result(
    named(gradingName, [finesNoun, ...otherCoarse, ...cobbles]),
    `${gradingSymbol}-${letter}${finesLetter}`,
  );
}
